import { defineStore } from 'pinia'
import { env } from '@shared/config/env'
import getDefaultFeatures from './helpers/app-config/features-config'
import {
  deepMap,
  setFeatureFlagsOnConfig,
  setWhitelabelsFeatures
} from './helpers/app-config/index.js'
import compose from 'just-compose'
import { whitelabels } from '@shared/constants'
import { restrictedFeaturesPaths } from '@shared/utils/feature-flags'
import { getNavigation } from './helpers/app-config/navigation'
import { useConfigurationStore } from '@shared/store/configuration'
import safeGet from 'just-safe-get'

function normalizeRoutePaths(items, path = '') {
  return items.map((item) => {
    const normalizedRoute = path + item.route

    return {
      ...item,
      fullRoute: normalizedRoute,
      children: item.children
        ? normalizeRoutePaths(item.children, normalizedRoute)
        : undefined
    }
  })
}

export const useAppConfigStore = defineStore('appConfig', {
  state: () => {
    return {
      featureLock: true,
      isSidebarCollapsed: false,
      _features: getDefaultFeatures()
    }
  },
  getters: {
    /**
     * @returns Object containging current feature flags. All flags will be true when featureLock is false.
     */
    featureConfig(state) {
      // if we want to toggle features, e.g. for QA turn all leaves to true
      if (!state.featureLock) {
        return deepMap(state._features, () => true)
      }
      return state._features
    },
    /**
     * @returns Object containing the navigation tree.
     */
    navigation() {
      const featureConfig = this.featureConfig
      const tenantConfig = useConfigurationStore().tenantConfig
      const dashboardsEnabled = safeGet(
        tenantConfig,
        'custom_dashboards.enabled',
        false
      )
      const customDashboards = dashboardsEnabled
        ? safeGet(tenantConfig, 'custom_dashboards.dashboards', null)
        : null

      const navigation = getNavigation({ featureConfig, customDashboards })

      return normalizeRoutePaths(navigation)
    }
  },
  actions: {
    /**
     * Flips the state of isSidebarCollapsed.
     */
    toggleSidebar() {
      this.isSidebarCollapsed = !this.isSidebarCollapsed
    },
    /**
     * @param {Object} features Feature flags configuration.
     * @param {string} whitelabel Name of current whitelabel.
     */
    setFeatureFlagsOnConfig(features, whitelabel) {
      const buildFeatures = compose(
        (config) => setFeatureFlagsOnConfig(config, features),
        setWhitelabelsFeatures(whitelabelsFeaturesConfig, whitelabel),
        (config) => setEnvDefaultFeatures(config, this.featureLock)
      )
      const defaults = getDefaultFeatures()
      const featureConfig = buildFeatures(defaults)

      this.$patch({ _features: featureConfig })
    }
  }
})

export const whitelabelsFeaturesConfig = {
  licenses: {
    path: 'resources.licenses',
    exclude: [whitelabels.LOCALSEARCH]
  },
  customers: {
    path: restrictedFeaturesPaths.crm[0],
    include: [whitelabels.LOCALSEARCH]
  }
}

const setEnvDefaultFeatures = (features, featureLock) => {
  if (!env.noDevFeatures && env.nodeEnv !== 'production' && !featureLock) {
    return deepMap(features, () => true)
  }
  return features
}
